import { Container, Graphics } from '../../../vendor/pixi.min.mjs';
import { SNAKE_CONFIG } from './snakeConfig.js';
import { drawTramCarRect } from '../../ui/TramCar.js';

const CAR_INSET = 2;
const CAR_GAP = 4;
const CAR_RADIUS = 8;
const CONNECTOR_COLOR = 0xb45309;
const TRAM_BODY_COLOR = 0xfacc15;
const TRAM_FRONT_COLOR = 0xeab308;
const TRAM_STRIPE_COLOR = 0xa16207;
const WINDOW_COLOR = 0xbfe7ff;

/**
 * Tram-style snake: each "car" is 2 cells long.
 * Cars are drawn as seamless rounded rectangles.
 * A thin connector joins adjacent cars.
 */
export class SnakeBody {
    /** @type {Container} */
    container = new Container();

    /** @type {{ col: number, row: number }[]} Index 0 = head */
    segments = [];

    /** Previous frame segment positions for smooth interpolation */
    _prevSegments = [];

    direction = { dc: 1, dr: 0 };
    _nextDirection = { dc: 1, dr: 0 };
    _growCounter = 0;
    smoothCorners = false;

    /** @type {Graphics[]} One per tram car */
    _carGfx = [];

    /** @type {Graphics} Connector lines between cars */
    _connectorGfx = new Graphics();
    _themeManager = null;

    constructor(startCol, startRow, length, themeManager = null) {
        this._themeManager = themeManager;
        for (let i = 0; i < length; i++) {
            this.segments.push({ col: startCol - i, row: startRow });
        }
        this._prevSegments = this.segments.map(s => ({ ...s }));
        this.container.addChild(this._connectorGfx);
    }

    setDirection(dc, dr) {
        if (dc === -this.direction.dc && dr === -this.direction.dr) return;
        if (dc === 0 && dr === 0) return;
        this._nextDirection = { dc, dr };
    }

    tick() {
        this._prevSegments = this.segments.map(s => ({ ...s }));

        this.direction = { ...this._nextDirection };
        const head = this.segments[0];
        const newHead = {
            col: head.col + this.direction.dc,
            row: head.row + this.direction.dr,
        };
        this.segments.unshift(newHead);

        if (this._growCounter > 0) {
            this._growCounter--;
        } else {
            this.segments.pop();
        }
        return newHead;
    }

    /** Queue growth of `amount` cells (default: GROW_AMOUNT from config) */
    grow(amount) {
        this._growCounter += (amount ?? SNAKE_CONFIG.GROW_AMOUNT);
    }

    get head() { return this.segments[0]; }

    checkSelfCollision() {
        const h = this.segments[0];
        for (let i = 1; i < this.segments.length; i++) {
            if (this.segments[i].col === h.col && this.segments[i].row === h.row) return true;
        }
        return false;
    }

    occupies(col, row) {
        for (const seg of this.segments) {
            if (seg.col === col && seg.row === row) return true;
        }
        return false;
    }

    /**
     * Render tram cars. Each car = 2 consecutive segments drawn as one shape.
     * @param {number} ox — originX
     * @param {number} oy — originY
     * @param {number} cs — cellSize
     * @param {number} [lerpT=0] — interpolation factor 0..1 (0 = no interpolation)
     * @param {number} [wrapCols=0] — grid columns for wrap-around clamping (0 = disabled)
     * @param {number} [wrapRows=0] — grid rows for wrap-around clamping (0 = disabled)
     */
    render(ox, oy, cs, lerpT = 0, wrapCols = 0, wrapRows = 0) {
        const segs = this._buildInterpolatedSegments(lerpT, wrapCols, wrapRows);
        const carCount = Math.ceil(segs.length / 2);

        while (this._carGfx.length < carCount) {
            const g = new Graphics();
            this._carGfx.push(g);
            this.container.addChild(g);
        }

        for (let ci = 0; ci < this._carGfx.length; ci++) {
            const g = this._carGfx[ci];
            g.clear();
            g.removeChildren();

            if (ci >= carCount) {
                g.visible = false;
                continue;
            }

            const isHead = ci === 0;
            const i1 = ci * 2;
            const i2 = ci * 2 + 1;
            const s1 = segs[i1];
            const s2 = i2 < segs.length ? segs[i2] : null;
            if (!s2) {
                this._drawSingleCell(g, ox, oy, cs, s1, this.direction, isHead);
            } else {
                this._drawCar(g, ox, oy, cs, s1, s2, isHead);
            }

            g.visible = true;
        }

        this._drawConnectors(ox, oy, cs, segs);
    }

    /**
     * Build interpolated segment positions for smooth rendering.
     * Falls back to discrete grid positions when lerpT is 0.
     */
    _buildInterpolatedSegments(lerpT, wrapCols, wrapRows) {
        if (lerpT <= 0 || this._prevSegments.length === 0) return this.segments;

        const prev = this._prevSegments;
        const curr = this.segments;
        const len = curr.length;
        const result = new Array(len);

        for (let i = 0; i < len; i++) {
            const c = curr[i];
            const p = i < prev.length ? prev[i] : c;

            let dc = c.col - p.col;
            let dr = c.row - p.row;

            if (wrapCols > 0 && Math.abs(dc) > wrapCols / 2) {
                dc = dc > 0 ? dc - wrapCols : dc + wrapCols;
            }
            if (wrapRows > 0 && Math.abs(dr) > wrapRows / 2) {
                dr = dr > 0 ? dr - wrapRows : dr + wrapRows;
            }

            result[i] = {
                col: p.col + dc * lerpT,
                row: p.row + dr * lerpT,
            };
        }

        return result;
    }

    reset(startCol, startRow, length) {
        this.segments.length = 0;
        for (let i = 0; i < length; i++) {
            this.segments.push({ col: startCol - i, row: startRow });
        }
        this._prevSegments = this.segments.map(s => ({ ...s }));
        this.direction = { dc: 1, dr: 0 };
        this._nextDirection = { dc: 1, dr: 0 };
        this._growCounter = 0;
    }

    destroy() {
        this.container.removeChildren();
        this._carGfx.length = 0;
        this.segments.length = 0;
        this._prevSegments.length = 0;
    }

    // ── drawing helpers ────────────────────────────────

    _drawCar(g, ox, oy, cs, s1, s2, isHead = false) {
        // ─── Unified PNG sprite path ────────────────────────────────
        // Works for ANY angle between s1 (front) and s2 (back) — including
        // the diagonal positions that happen mid-turn during smooth
        // interpolation. The sprite is drawn at the midpoint, sized to
        // the full 2-cell length × 1-cell width (matching the procedural
        // rect math), and rotated by atan2(forwardVec) so the tram's
        // "front" always points where it's heading.
        const x1 = ox + s1.col * cs + cs / 2;
        const y1 = oy + s1.row * cs + cs / 2;
        const x2 = ox + s2.col * cs + cs / 2;
        const y2 = oy + s2.row * cs + cs / 2;
        const dx = x1 - x2;
        const dy = y1 - y2;
        const segDist = Math.sqrt(dx * dx + dy * dy);

        const slot = isHead ? 'snake.tram.head' : 'snake.tram.body';
        const carLen = segDist + cs - CAR_INSET * 2;
        const carW   = cs - CAR_INSET * 2;
        const sprite = this._themeManager?.createSprite(slot, carLen, carW, {
            fitMode: 'stretch',
        });
        if (sprite) {
            sprite.x = (x1 + x2) / 2;
            sprite.y = (y1 + y2) / 2;
            // Source art faces right; rotating by the forward angle
            // makes it face up/down/left/right and every angle between
            // for smooth-corner interpolation.
            sprite.rotation = segDist > 0.5 ? Math.atan2(dy, dx) : 0;
            g.addChild(sprite);
            return;
        }

        // ─── Procedural fallback (unchanged behaviour) ──────────────
        const dc = Math.abs(s1.col - s2.col);
        const dr = Math.abs(s1.row - s2.row);
        const horizontal = dc >= dr;

        if (this.smoothCorners && dc > 0.01 && dr > 0.01) {
            this._drawCornerCapsule(g, ox, oy, cs, s1, s2);
            return;
        }

        if (horizontal && dr < 0.5) {
            const minCol = Math.min(s1.col, s2.col);
            const avgRow = (s1.row + s2.row) / 2;
            const span = dc + 1;
            const rect = {
                x: ox + minCol * cs + CAR_INSET,
                y: oy + avgRow * cs + CAR_INSET,
                w: cs * span - CAR_INSET * 2,
                h: cs - CAR_INSET * 2,
            };
            const dir = s1.col >= s2.col ? -1 : 1;
            this._drawTramRect(g, rect, true, dir, isHead);
        } else if (!horizontal && dc < 0.5) {
            const minRow = Math.min(s1.row, s2.row);
            const avgCol = (s1.col + s2.col) / 2;
            const span = dr + 1;
            const rect = {
                x: ox + avgCol * cs + CAR_INSET,
                y: oy + minRow * cs + CAR_INSET,
                w: cs - CAR_INSET * 2,
                h: cs * span - CAR_INSET * 2,
            };
            const dir = s1.row >= s2.row ? -1 : 1;
            this._drawTramRect(g, rect, false, dir, isHead);
        } else {
            const dir = {
                dc: Math.sign(s1.col - s2.col),
                dr: Math.sign(s1.row - s2.row),
            };
            this._drawSingleCell(g, ox, oy, cs, s1, dir, isHead);
            this._drawSingleCell(g, ox, oy, cs, s2, dir, false);
        }
    }

    _drawSingleCell(g, ox, oy, cs, seg, dir, isHead = false) {
        const horizontal = Math.abs(dir.dc) >= Math.abs(dir.dr);
        const forward = horizontal ? (dir.dc || 1) : (dir.dr || 1);
        this._drawTramRect(g, {
            x: ox + seg.col * cs + CAR_INSET,
            y: oy + seg.row * cs + CAR_INSET,
            w: cs - CAR_INSET * 2,
            h: cs - CAR_INSET * 2,
        }, horizontal, forward, isHead);
    }

    _drawCornerCapsule(g, ox, oy, cs, s1, s2) {
        const x1 = ox + s1.col * cs + cs / 2;
        const y1 = oy + s1.row * cs + cs / 2;
        const x2 = ox + s2.col * cs + cs / 2;
        const y2 = oy + s2.row * cs + cs / 2;

        const halfW = (cs - CAR_INSET * 2) / 2;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const len = Math.sqrt(dx * dx + dy * dy);

        if (len < 1) {
            g.circle(x1 + 2, y1 + 2, halfW);
            g.fill({ color: 0x000000, alpha: 0.2 });
            g.circle(x1, y1, halfW);
            g.fill(TRAM_BODY_COLOR);
            return;
        }

        const ux = dx / len;
        const uy = dy / len;
        const nx = -uy * halfW;
        const ny = ux * halfW;

        g.moveTo(x1 + nx + 2, y1 + ny + 2);
        g.lineTo(x2 + nx + 2, y2 + ny + 2);
        g.lineTo(x2 - nx + 2, y2 - ny + 2);
        g.lineTo(x1 - nx + 2, y1 - ny + 2);
        g.closePath();
        g.fill({ color: 0x000000, alpha: 0.15 });

        g.moveTo(x1 + nx, y1 + ny);
        g.lineTo(x2 + nx, y2 + ny);
        g.lineTo(x2 - nx, y2 - ny);
        g.lineTo(x1 - nx, y1 - ny);
        g.closePath();
        g.fill(TRAM_BODY_COLOR);

        g.circle(x1, y1, halfW);
        g.fill(TRAM_BODY_COLOR);
        g.circle(x2, y2, halfW);
        g.fill(TRAM_BODY_COLOR);

        const frontR = halfW * 0.6;
        g.circle(x1, y1, frontR);
        g.fill(TRAM_FRONT_COLOR);

        const mx = (x1 + x2) / 2;
        const my = (y1 + y2) / 2;
        const sLen = len * 0.38;
        g.moveTo(mx - ux * sLen + nx * 0.07, my - uy * sLen + ny * 0.07);
        g.lineTo(mx + ux * sLen + nx * 0.07, my + uy * sLen + ny * 0.07);
        g.lineTo(mx + ux * sLen - nx * 0.07, my + uy * sLen - ny * 0.07);
        g.lineTo(mx - ux * sLen - nx * 0.07, my - uy * sLen - ny * 0.07);
        g.closePath();
        g.fill(TRAM_STRIPE_COLOR);

        const winR = Math.max(2, halfW * 0.2);
        const winSpacing = Math.max(cs * 0.25, len * 0.2);
        for (let i = -1; i <= 1; i++) {
            const wx = mx + ux * winSpacing * i;
            const wy = my + uy * winSpacing * i;
            g.circle(wx, wy, winR);
            g.fill(WINDOW_COLOR);
        }
    }

    _drawTramRect(g, rect, horizontal, forwardSign, isHead = false) {
        // PNG path: try `snake.tram.head` for the first car and
        // `snake.tram.body` for every other car. Source art is authored
        // facing right; we flip horizontally for left-facing motion and
        // rotate ±90° for vertical motion so the front always points the
        // way the tram is travelling.
        const slot = isHead ? 'snake.tram.head' : 'snake.tram.body';
        const sprite = horizontal
            ? this._themeManager?.createSprite(slot, rect.w, rect.h, {
                fitMode: 'stretch',
                flipX: forwardSign < 0,
            })
            // Vertical: build the sprite at "horizontal" dimensions
            // (rect.h × rect.w), then rotate ±90° around its centre so
            // the visual ends up filling the cell's actual rect.w × rect.h.
            : this._themeManager?.createSprite(slot, rect.h, rect.w, {
                fitMode: 'stretch',
            });

        if (sprite) {
            sprite.x = rect.x + rect.w / 2;
            sprite.y = rect.y + rect.h / 2;
            if (!horizontal) {
                sprite.rotation = forwardSign > 0 ? Math.PI / 2 : -Math.PI / 2;
            }
            g.addChild(sprite);
            return;
        }

        drawTramCarRect(g, rect, {
            horizontal,
            forwardSign,
            windows: 3,
            windowHighlights: true,
            topHighlight: true,
            bodyColor: TRAM_BODY_COLOR,
            frontColor: TRAM_FRONT_COLOR,
            stripeColor: TRAM_STRIPE_COLOR,
            windowColor: WINDOW_COLOR,
            radius: Math.min(CAR_RADIUS, Math.floor(Math.min(rect.w, rect.h) * 0.25)),
        });
    }

    /** Draw small connector rectangles between adjacent tram cars */
    _drawConnectors(ox, oy, cs, segs = null) {
        this._connectorGfx.clear();
        this._connectorGfx.removeChildren();
        const segments = segs || this.segments;

        const carCount = Math.ceil(segments.length / 2);
        if (carCount < 2) return;

        const connW = 6;
        const connH = Math.floor(cs * 0.35);

        for (let ci = 0; ci < carCount - 1; ci++) {
            const tailIdx = ci * 2 + 1;
            const nextIdx = tailIdx + 1;
            if (nextIdx >= segments.length) continue;

            const tail = segments[tailIdx];
            const next = segments[nextIdx];

            const dc = next.col - tail.col;
            const dr = next.row - tail.row;

            const midCol = (tail.col + next.col) / 2;
            const midRow = (tail.row + next.row) / 2;

            const horizontal = Math.abs(dc) >= Math.abs(dr);
            let cx, cy, cw, ch;

            if (horizontal) {
                cx = ox + midCol * cs + cs / 2 - connW / 2;
                cy = oy + midRow * cs + (cs - connH) / 2;
                cw = connW;
                ch = connH;
            } else {
                cx = ox + midCol * cs + (cs - connH) / 2;
                cy = oy + midRow * cs + cs / 2 - connW / 2;
                cw = connH;
                ch = connW;
            }

            if (Math.abs(dc) > 0.01 || Math.abs(dr) > 0.01) {
                const sprite = this._themeManager?.createSprite('snake.tram.connector', cw, ch, {
                    fitMode: 'cover',
                });
                if (sprite) {
                    sprite.x = cx + cw / 2;
                    sprite.y = cy + ch / 2;
                    this._connectorGfx.addChild(sprite);
                } else {
                    this._connectorGfx.roundRect(cx, cy, cw, ch, 2);
                    this._connectorGfx.fill(CONNECTOR_COLOR);
                }
            }
        }
    }
}
